/**
 * State for the Thread surface - the product's main interaction - shared through
 * `useModel('threadContext')`.
 *
 * Covers both halves of the surface:
 * - the thread *list* in the sidebar (cursor-paged, dedupe-merged, searchable);
 * - the thread *transcript*, loaded question by question and streamed out to the
 *   page through callbacks so it can paint progressively.
 *
 * It also holds the per-thread lookup tables for the people involved (responders
 * and recommenders), which the answer views populate as content streams in.
 *
 * Roughly 25 call sites, and a single shared instance, so treat the state here
 * as global: `doClean` exists precisely because a screen cannot assume it starts
 * from an empty model.
 */
import { useState, useRef } from 'react';
import {
  createThread,
  getThreads,
  searchThreads,
  getThread,
  getQuestionsByThreadId,
  getCandidatesByQuestionId,
  createQuestion,
  createCompareQuestion,
  getQuestionClientId,
  createGeroscienceAngleCandidate,
  getCandidateById,
} from '@/services/rest/api';
import { useModel } from '@umijs/max';

export default () => {
  /** Thread the user is currently looking at; read by the sidebar for highlighting. */
  const currentThreadIdRef = useRef<string>();
  // In-flight guards. Refs rather than state because they must be readable
  // synchronously at the top of the loader to reject a re-entrant call, before
  // any render could deliver a state update.
  const loadingThreadListRef = useRef<boolean>(false);
  const loadingThreadItemRef = useRef<boolean>(false);

  /** Person the composer is currently addressing, if any. */
  const selectedResponderRef = useRef<DATA.V2Influencer>();

  /**
   * One-shot handover slot from the composer to the thread page.
   *
   * Creating a thread navigates away immediately, but the people the user picked
   * are already resolved here and would otherwise have to be re-fetched on the
   * new page. They are stashed under the new thread id and consumed exactly once
   * by `getInit` - hence the clear on read, and the id check that stops a stale
   * payload leaking into a different thread.
   */
  const initRef = useRef<{ threadId: string; influencers: DATA.V2Influencer[] }>();

  const [threads, setThreads] = useState<DATA.V2Thread[]>([]);
  /** id -> thread, the dedupe index behind `mergeThreads`. */
  const idMapRef = useRef(new Map<string, DATA.V2Thread>());
  const limitRef = useRef<number>(10);
  /** Pagination cursor: `createdAt` of the oldest thread loaded so far. */
  const dateRef = useRef<Date | undefined>(undefined);
  const hasMoreRef = useRef<boolean>(true);

  /** Current search text; empty means the list shows everything. */
  const [query, setQuery] = useState<string>();

  const { doGetInfluencersByIds } = useModel('influencerContext');

  // Per-thread lookup tables for the people involved: those who answered, and
  // those suggested as recommenders (with a mention count and whether the
  // mention came from a reference). Populated as answers stream in.
  const responderIdMapRef = useRef(new Map<string, DATA.V2Influencer>());
  const recommenderIdMapRef = useRef(
    new Map<string, { count: number; entity: DATA.V2Influencer; isRef: boolean }>(),
  );

  // The recommender map is a ref, so mutating it renders nothing. Bumping this
  // counter is how writers tell the recommender rail to re-read the map.
  const [updatedRecommanderCount, setUpdatedRecommanderCount] = useState<number>(0);

  /** Stash the composer's resolved people for the thread page about to mount. */
  const setInit = (currentThreadId: string, influencers: DATA.V2Influencer[]) => {
    initRef.current = { threadId: currentThreadId, influencers };
  };

  /** Consume the stash, but only if it belongs to this thread. */
  const getInit = (currentThreadId: string, success: (objs: DATA.V2Influencer[]) => void) => {
    if (initRef.current && currentThreadId === initRef.current.threadId) {
      success(initRef.current.influencers);
      initRef.current = undefined;
    }
  };

  /**
   * Fold a freshly loaded page into the list, deduping by id and re-sorting
   * newest-first. Going through the map means a thread that appears in both a
   * search result and a paged load is stored once.
   */
  const mergeThreads = (newObjs: DATA.V2Thread[]) => {
    newObjs.forEach((item) => idMapRef.current.set(item.id, item));
    const merged = Array.from(idMapRef.current.values());
    merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    return merged;
  };

  /** Open a new thread; the caller navigates to the id it hands back. */
  const doCreateThread = async (
    success: (threadId: string) => void,
    failed: () => void,
    rawTitle?: string,
    influencerIds?: string[],
    searchMode?: 'fast' | 'deep',
  ) => {
    try {
      const msg = await createThread(rawTitle, influencerIds, searchMode, {
        skipErrorHandler: true,
      });
      if (msg.success && msg.data) {
        success(msg.data.id);
      } else {
        failed();
      }
    } catch (error) {
      failed();
      console.error('Error create thread:', error);
    } finally {
    }
  };

  /** Reset list state - search text, loaded pages and cursor - back to empty. */
  const doClean = async () => {
    setQuery(undefined);
    setThreads([]);
    dateRef.current = undefined;
    idMapRef.current.clear();
    hasMoreRef.current = true;
  };

  /**
   * Load the next page of threads.
   *
   * Paginates on `createdAt` rather than an offset, so threads created while
   * the user scrolls cannot shift the window and cause skips or repeats. A
   * short page is the end-of-list signal.
   */
  const doGetThreadList = async () => {
    if (loadingThreadListRef.current || !hasMoreRef.current) return;
    loadingThreadListRef.current = true;

    try {
      const msg = await getThreads(dateRef.current, limitRef.current, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        const objs = msg.data.map((obj) => {
          const parsed = {
            ...obj,
            createdAt: new Date(obj.createdAt),
            updatedAt: new Date(obj.updatedAt),
          };
          idMapRef.current.set(parsed.id, parsed);
          return parsed;
        });

        if (objs.length < limitRef.current) {
          hasMoreRef.current = false;
        }

        const merged = mergeThreads(objs);
        if (merged.length > 0) dateRef.current = merged[merged.length - 1].createdAt;

        setThreads(merged);
      }
    } catch (error) {
      console.error('Error fetching home pages:', error);
    } finally {
      loadingThreadListRef.current = false;
    }
  };

  /**
   * Replace the list with search results.
   *
   * Resets the pagination state up front: results are their own single page, and
   * leaving the old cursor in place would let a later `doGetThreadList` splice
   * unrelated threads into them.
   */
  const doSearchThreadList = async () => {
    if (loadingThreadListRef.current || !query || query.length === 0) return;
    loadingThreadListRef.current = true;
    idMapRef.current.clear();
    dateRef.current = undefined;
    hasMoreRef.current = true;

    try {
      const msg = await searchThreads(query, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        const objs = msg.data.map((obj) => {
          const parsed = {
            ...obj,
            createdAt: new Date(obj.createdAt),
            updatedAt: new Date(obj.updatedAt),
          };
          idMapRef.current.set(parsed.id, parsed);
          return parsed;
        });
        setThreads(objs);
      }
    } catch (error) {
      console.error('Error fetching home pages:', error);
    } finally {
      loadingThreadListRef.current = false;
    }
  };

  /**
   * Load a full thread transcript, streaming it out through callbacks rather
   * than returning it.
   *
   * Answers are fetched per question, so a long thread is many sequential round
   * trips; emitting through `onList` in batches of three lets the page paint
   * progressively instead of waiting for the last one.
   *
   * `onWorking` is the special case for a thread whose only question is still
   * being answered - it renders as a live placeholder rather than as transcript.
   */
  const doGetThreadItem = async (
    threadId: string,
    onThread: (obj: DATA.V2Thread) => void,
    onWorking: (obj: DATA.V2Question) => void,
    onList: (objs: DATA.V2Question[]) => void,
    failed: () => void,
  ) => {
    if (loadingThreadItemRef.current) return;
    loadingThreadItemRef.current = true;

    try {
      const msg = await getThread(threadId, { skipErrorHandler: true });

      if (msg && msg.data) {
        const threadIds = new Set(threads.map((v) => v.id));
        if (!threadIds.has(msg.data.id)) {
          setThreads([msg.data, ...threads]);
        }

        onThread(msg.data);
        const first = await getQuestionsByThreadId(threadId, { skipErrorHandler: true });

        if (first.success && first.data) {
          const parsedQuestions = first.data
            .map((question) => {
              const parsedQuestion = {
                ...question,
                createdAt: new Date(question.createdAt),
                updatedAt: new Date(question.updatedAt),
              };
              return parsedQuestion;
            })
            .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

          const goods: DATA.V2Question[] = [];
          for (const question of parsedQuestions) {
            const parsedQuestion = {
              ...question,
              createdAt: new Date(question.createdAt),
              updatedAt: new Date(question.updatedAt),
            };

            const second = await getCandidatesByQuestionId(parsedQuestion.id);
            if (second.success && second.data) {
              const candidates = second.data.map((canidate) => {
                const parsedCanidate = {
                  ...canidate,
                  createdAt: new Date(canidate.createdAt),
                  updatedAt: new Date(canidate.updatedAt),
                };
                return parsedCanidate;
              });
              parsedQuestion.candidates = candidates;
            }

            if (
              parsedQuestions.length === 1 &&
              question.isHead &&
              question.status === 'PUBLISHED'
            ) {
              onWorking(parsedQuestion);
            } else {
              goods.push(parsedQuestion);
            }
            // Flush in small batches so the transcript appears as it loads.
            if (goods.length >= 3) {
              onList([...goods]);
              goods.length = 0;
            }
          }

          // Final partial batch.
          if (goods.length > 0) {
            onList([...goods]);
            goods.length = 0;
          }
        } else {
          failed();
        }
      } else {
        failed();
      }
    } catch (error) {
      failed();
      console.error('Error fetching home pages:', error);
    } finally {
      loadingThreadItemRef.current = false;
    }
  };

  /**
   * Post a question into a thread. The response confirms acceptance; the answer
   * itself arrives over SSE, keyed by the client id from
   * `doGetQuestionClientId`.
   */
  const doCreateQuestion = async (
    success: (question: DATA.V2Question, candidates: DATA.V2Candidate[]) => void,
    failed: () => void,
    threadId: string,
    rawTitle?: string,
    influencerIds?: string[],
    searchMode?: 'fast' | 'deep',
  ) => {
    try {
      const msg = await createQuestion(threadId, rawTitle, influencerIds, searchMode, {
        skipErrorHandler: true,
      });
      if (msg.success && msg.data) {
        const parsedQuestion = {
          ...msg.data.question,
          createdAt: new Date(msg.data.question.createdAt),
          updatedAt: new Date(msg.data.question.updatedAt),
        };

        const parsedCandidates = msg.data.candidates.map((candidate) => {
          return {
            ...candidate,
            createdAt: new Date(candidate.createdAt),
            updatedAt: new Date(candidate.updatedAt),
          };
        });

        success(parsedQuestion, parsedCandidates);
      } else {
        failed();
      }
    } catch (error) {
      failed();
      console.error('Error fetching home pages:', error);
    } finally {
      loadingThreadItemRef.current = false;
    }
  };

  /**
   * Ask for a structured comparison of 2-3 already-cited sources.
   *
   * Mirrors doCreateQuestion's success/failed/question/candidates shape so the
   * caller can append the result into the same question list the same way -
   * only the underlying API call and its body differ.
   */
  const doCreateCompareQuestion = async (
    success: (question: DATA.V2Question, candidates: DATA.V2Candidate[]) => void,
    failed: () => void,
    threadId: string,
    organicResultIds: string[],
    rawTitle: string,
  ) => {
    try {
      const msg = await createCompareQuestion(threadId, organicResultIds, rawTitle, {
        skipErrorHandler: true,
      });
      if (msg.success && msg.data) {
        const parsedQuestion = {
          ...msg.data.question,
          createdAt: new Date(msg.data.question.createdAt),
          updatedAt: new Date(msg.data.question.updatedAt),
        };

        const parsedCandidates = msg.data.candidates.map((candidate) => {
          return {
            ...candidate,
            createdAt: new Date(candidate.createdAt),
            updatedAt: new Date(candidate.updatedAt),
          };
        });

        success(parsedQuestion, parsedCandidates);
      } else {
        failed();
      }
    } catch (error) {
      failed();
      console.error('Error creating compare question:', error);
    } finally {
      loadingThreadItemRef.current = false;
    }
  };

  /**
   * Claim the SSE subscription handle for a question's answer stream.
   *
   * The backend enforces one subscriber per question (QuestionSubscription's
   * unique index on questionId) and answers a conflicting claim with 409 -
   * its own docstring says the caller is expected to retry. A previous
   * subscriber's slot is normally freed within moments of its connection
   * closing (SSEManager's `res.on('close', ...)` deletes the reservation),
   * so a 409 right after opening this same question elsewhere (e.g. a
   * reload, or two tabs on the same thread) is almost always transient, not
   * a real conflict - it clears up well inside this backoff window in the
   * ordinary case. Retrying is bounded rather than unconditional so a 409
   * that does NOT clear (the rare case: the previous reservation was never
   * connected and is waiting out its own TTL) gives up instead of hammering
   * the endpoint - the caller's `failed` falls back to the thread's existing
   * behavior for an unreachable stream (2026-10-19 - without this, 409 was
   * swallowed silently and the answer only ever appeared after a manual
   * refresh, once the question had finished and no longer needed a stream).
   */
  const doGetQuestionClientId = async (
    questionId: string,
    success: (id: string) => void,
    failed: () => void,
  ) => {
    const retryDelaysMs = [1000, 2000, 4000, 8000];

    for (let attempt = 0; ; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const msg = await getQuestionClientId(questionId, { skipErrorHandler: true });

        if (msg.success && msg.data) {
          success(msg.data.clientId);
          return;
        }
        failed();
        return;
      } catch (error: any) {
        const isConflict = error?.response?.status === 409;
        if (isConflict && attempt < retryDelaysMs.length) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(resolve, retryDelaysMs[attempt]);
          });
          // eslint-disable-next-line no-continue
          continue;
        }
        failed();
        console.error('Error fetching home pages:', error);
        return;
      }
    }
  };

  /**
   * Create (or return the existing) `-2` geroscience-angle candidate for a
   * question - Youth Button's click target.
   *
   * Creation only, no retry loop like doGetQuestionClientId's: this is a
   * plain upsert keyed on questionId+influencerId (schema.prisma's unique
   * constraint), not a single-use subscription claim, so there is no 409 to
   * race against. The caller still has to call doGetQuestionClientId and
   * reconnect to the question's own SSE stream afterward for generation to
   * actually start - this call alone does not trigger anything.
   */
  const doCreateGeroscienceAngleCandidate = async (
    questionId: string,
    success: (candidate: DATA.V2Candidate) => void,
    failed: () => void,
  ) => {
    try {
      const msg = await createGeroscienceAngleCandidate(questionId, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        success(msg.data.candidate);
      } else {
        failed();
      }
    } catch (error) {
      failed();
      console.error('Error creating geroscience angle candidate:', error);
    }
  };

  /** Re-read one answer, e.g. after its stream reports completion. */
  const doGetCandidate = async (
    candidateId: string,
    success: (obj: DATA.V2Candidate) => void,
    failed: () => void,
  ) => {
    try {
      const msg = await getCandidateById(candidateId, { skipErrorHandler: true });

      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      failed();
      console.error('Error doGetCandidate:', error);
    }
  };

  /**
   * Resolve a person mentioned in a thread, cheapest source first: the responder
   * map, then the recommender map, then the shared people cache.
   *
   * The trailing `pointTo` hop follows a merge redirect - ids in older answers
   * can name records that have since been merged into another - so the UI always
   * links to the surviving record.
   */
  const doGetInfluencerFromMap = async (influencerId: string) => {
    let firstFound = responderIdMapRef.current.get(influencerId);
    if (!firstFound) {
      const second = recommenderIdMapRef.current.get(influencerId);
      if (second) {
        firstFound = second.entity;
      }
    }
    if (!firstFound) {
      await doGetInfluencersByIds(
        [influencerId],
        (obj) => {
          if (obj.length > 0) {
            firstFound = obj[0];
          }
        },
        () => {},
        true,
      );
    }

    if (firstFound && firstFound.pointTo) {
      await doGetInfluencersByIds(
        [firstFound.pointTo],
        (obj) => {
          if (obj.length > 0) {
            firstFound = obj[0];
          }
        },
        () => {},
        true,
      );
    }

    return firstFound;
  };

  return {
    getInit,
    setInit,
    currentThreadIdRef,
    selectedResponderRef,
    updatedRecommanderCount,
    setUpdatedRecommanderCount,
    responderIdMapRef,
    recommenderIdMapRef,
    doGetInfluencerFromMap,
    threads,
    setThreads,
    query,
    setQuery,
    doClean,
    doCreateThread,
    doGetThreadList,
    doSearchThreadList,
    doGetThreadItem,
    doCreateQuestion,
    doCreateCompareQuestion,
    doGetQuestionClientId,
    doCreateGeroscienceAngleCandidate,
    doGetCandidate,
  };
};
